import type { FlipFase } from './faseFlip';
import { fmtDateShort } from './vorderingHelpers';

interface PlanningFlipPreviewProps {
  fases: FlipFase[];
  /** The fase an einde-werken create marks «einde werken» (null: none to flip). */
  lastFlipId: number | null;
  eindeWerken: boolean;
  /** True when ticked collectors are in the selection (picks the empty-state copy). */
  hasCollectors: boolean;
}

/**
 * Planning-flip PREVIEW: which fases the create auto-marks (ADR-064, supersedes
 * ADR-052 guide-only for the dashboard). Future / undated fases stay as-is. The
 * list covers the fases of ticked collectors and of ticked interventie groups.
 */
export function PlanningFlipPreview({ fases, lastFlipId, eindeWerken, hasCollectors }: PlanningFlipPreviewProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Planning bijwerken</p>
      <p className="mt-0.5 text-[11px] text-td-light">
        Bij het aanmaken gaan de planning-taken van <span className="font-semibold">vandaag of in het verleden</span> automatisch op «gevorderd»{eindeWerken && <> en wordt de <span className="font-semibold">laatste fase van vandaag/het verleden</span> op «einde werken» gezet</>}.
        Toekomstige taken blijven ongewijzigd.
      </p>
      {fases.length > 0 ? (
        <ul className="mt-2 space-y-1.5 text-sm">
          {fases.map((f) => {
            const label = f.count === 0 && f.interventieCount > 0 ? `${f.name} (interventie)` : f.name;
            return (
              <li key={f.id} className="flex items-center gap-2">
                {f.undated ? (
                  <>
                    <span className="font-bold text-td-light" aria-hidden="true">·</span>
                    <span className="text-td-muted">{label}</span>
                    <span className="text-td-light">— geen planningsdatum → niets te markeren</span>
                  </>
                ) : f.isFuture ? (
                  <>
                    <span className="font-bold text-amber-600" aria-hidden="true">⊘</span>
                    <span className="text-td-muted">{label}</span>
                    <span className="text-td-light">— gepland {fmtDateShort(f.date)} (toekomst) → blijft ongewijzigd</span>
                  </>
                ) : eindeWerken && f.id === lastFlipId ? (
                  <>
                    <span className="font-bold text-td-navy" aria-hidden="true">⚑</span>
                    <span className="text-td-navy">{label}</span>
                    <span className="text-td-light">— gepland {fmtDateShort(f.date)} → «einde werken»</span>
                  </>
                ) : (
                  <>
                    <span className="font-bold text-td-green" aria-hidden="true">✓</span>
                    <span className="text-td-navy">{label}</span>
                    <span className="text-td-light">— gepland {fmtDateShort(f.date)} → «gevorderd»</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-[11px] text-td-light">
          {hasCollectors
            ? 'Geselecteerde collectoren hebben geen gekoppelde fase — er is niets om in de planning te markeren.'
            : 'Deze oudere interventies hebben geen gekoppelde fase — er is niets om in de planning te markeren.'}
        </p>
      )}
    </div>
  );
}
